import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Card } from "@chakra-ui/react";
import { FiBarChart2, FiTrendingUp, FiPieChart } from "react-icons/fi";
import { formatCurrency, formatNumber, isCurrencyColumn } from "../../lib/formatters";

const COLORS = ["#0142B1", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D"];

interface ChartRendererProps {
  type: "bar_chart" | "line_chart" | "pie_chart";
  title: string;
  data: any[];
  config: {
    x_key?: string;
    y_key?: string;
    label_key?: string;
    value_key?: string;
    // Legacy support
    xKey?: string;
    yKey?: string;
    nameKey?: string;
    valueKey?: string;
    [key: string]: any;
  };
}

export const ChartRenderer = ({ type, title, data: rawData, config }: ChartRendererProps) => {
  // Pre-process data to handle null values in keys
  const data = rawData?.map(item => {
    const xKey = config.x_key || config.xKey || "name";
    if (item && (item[xKey] === null || item[xKey] === undefined)) {
      return { ...item, [xKey]: "Unknown" };
    }
    return item;
  }) || [];

  // Handle empty data
  if (!data || data.length === 0) {
    const getEmptyStateIcon = () => {
      switch (type) {
        case "bar_chart":
          return <FiBarChart2 className="text-4xl text-gray-300" />;
        case "line_chart":
          return <FiTrendingUp className="text-4xl text-gray-300" />;
        case "pie_chart":
          return <FiPieChart className="text-4xl text-gray-300" />;
        default:
          return <FiBarChart2 className="text-4xl text-gray-300" />;
      }
    };

    return (
      <Card.Root className="p-6! h-[350px] w-full bg-white shadow-sm border border-gray-100 rounded-xl">
        <div className="mb-4">
          <h3 className="text-sm! font-semibold! text-gray-700">{title}</h3>
        </div>
        <div className="flex flex-col items-center justify-center h-[280px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <div className="mb-3">{getEmptyStateIcon()}</div>
          <p className="text-gray-500 font-medium">No data available</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your query or date range</p>
        </div>
      </Card.Root>
    );
  }

  // Custom tooltip formatter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatTooltipValue = (value: any, name?: any) => {
    if (typeof value === "number") {
      const yKey = config.y_key || config.yKey || config.value_key || config.valueKey || "value";
      if (isCurrencyColumn(yKey) || (name && isCurrencyColumn(name))) {
        return formatCurrency(value);
      }
      return formatNumber(value);
    }
    return value;
  };

  const renderChart = () => {
    switch (type) {
      case "bar_chart":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey={config.x_key || config.xKey || "name"} 
                tick={{ fontSize: 12 }} 
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12 }} 
                tickLine={false}
                axisLine={false}
                width={45}
                domain={[0, 'auto']}
                tickFormatter={(value) => 
                  new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(value)
                }
              />
              <Tooltip 
                cursor={{ fill: '#f3f4f6' }}
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                formatter={formatTooltipValue}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} />
              <Bar 
                dataKey={config.y_key || config.yKey || "value"} 
                fill="#0142B1" 
                radius={[4, 4, 0, 0]} 
                maxBarSize={60}
                minPointSize={2}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case "line_chart":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey={config.x_key || config.xKey || "name"} 
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12 }} 
                tickLine={false}
                axisLine={false}
                width={45}
                domain={['auto', 'auto']}
                tickFormatter={(value) => 
                  new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(value)
                }
              />
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                formatter={formatTooltipValue}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} />
              <Line
                type="monotone"
                dataKey={config.y_key || config.yKey || "value"}
                stroke="#0142B1"
                strokeWidth={3}
                dot={{ r: 4, fill: "#0142B1", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case "pie_chart":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey={config.value_key || config.valueKey || "value"}
                nameKey={config.label_key || config.nameKey || "name"}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                formatter={formatTooltipValue}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <Card.Root className="p-4! h-[350px] w-full bg-white shadow-sm border border-gray-100 rounded-xl overflow-hidden">
      <div className="mb-4">
        <h3 className="text-sm! font-semibold! text-gray-700">{title}</h3>
      </div>
      <div className="h-[280px] w-full">
        {renderChart()}
      </div>
    </Card.Root>
  );
};
